import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, ArrowRight, ShieldAlert, Globe } from 'lucide-react';
import {
  ALL_LOCATIONS,
  US_LOCATIONS,
  CANADA_LOCATIONS,
  UK_LOCATIONS,
  AUSTRALIA_LOCATIONS,
  COUNTRY_CONFIGS,
  LocationRegion,
} from '../services/locationData';
import { CountryCode } from '../types/alert';
import { APP_CONFIG } from '../config/appConfig';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';

export const LocationsOverviewPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<CountryCode>('US');

  const getDataset = () => {
    switch (activeTab) {
      case 'CA':
        return CANADA_LOCATIONS;
      case 'GB':
        return UK_LOCATIONS;
      case 'AU':
        return AUSTRALIA_LOCATIONS;
      default:
        return US_LOCATIONS;
    }
  };

  const currentDataset = getDataset();

  const filtered = currentDataset.filter(
    (loc) =>
      loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loc.keyCities.some((c) => c.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Page Header */}
      <div className="max-w-3xl space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-cyan-300 text-xs font-mono font-bold">
          <Globe className="w-3.5 h-3.5" />
          <span>GLOBAL JURISDICTION DIRECTORY</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-black text-navy-950 font-sans">
          International States, Provinces &amp; Regional Directory
        </h1>
        <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-sans">
          Access verified official emergency and severe weather radars for the United States, Canada, the United Kingdom, and Australia.
        </p>

        {/* Country Selector Tabs */}
        <div className="pt-3 flex flex-wrap gap-2">
          {(['US', 'CA', 'GB', 'AU'] as CountryCode[]).map((code) => {
            const conf = COUNTRY_CONFIGS[code];
            return (
              <button
                key={code}
                onClick={() => setActiveTab(code)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border ${
                  activeTab === code
                    ? 'bg-navy-950 text-white border-navy-900 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <span>{conf.flag}</span>
                <span>{conf.name}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="pt-3 max-w-md relative">
          <label htmlFor="search-state" className="sr-only">Filter regions or cities</label>
          <input
            id="search-state"
            type="text"
            placeholder={`Filter ${COUNTRY_CONFIGS[activeTab].name} regions (e.g. Ontario, Texas, London, Sydney)...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs font-sans"
          />
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
        </div>
      </div>

      {/* Directory Grid */}
      <section aria-labelledby="regions-list-heading" className="space-y-4">
        <h2 id="regions-list-heading" className="text-lg font-bold text-navy-950 flex items-center justify-between border-b border-slate-200 pb-2">
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span>
              {COUNTRY_CONFIGS[activeTab].flag} {COUNTRY_CONFIGS[activeTab].name} ({filtered.length} regions)
            </span>
          </span>
          <span className="text-xs font-mono text-slate-500">
            Official Source: {COUNTRY_CONFIGS[activeTab].primaryAgency}
          </span>
        </h2>

        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 bg-white border border-slate-200 rounded-2xl">
            No matching regions found in {COUNTRY_CONFIGS[activeTab].name} for "{searchTerm}".
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((loc) => (
              <Link
                key={loc.code}
                to={`/location/${loc.code}`}
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-cyan-500 hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-navy-950 text-base group-hover:text-blue-600">
                      {loc.name}
                    </span>
                    <span className="text-xs font-mono font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                      {loc.code}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Capital: <strong className="text-slate-700">{loc.capital}</strong>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-2 truncate">
                    Key Hubs: {loc.keyCities.slice(0, 3).join(', ')}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-blue-600 group-hover:text-blue-800">
                  <span>Open Radar &rarr;</span>
                  <span className="text-[10px] font-mono text-slate-400 uppercase">
                    {loc.region}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Ad slot */}
      <AdSlot slotId="locations-bottom" format="horizontal" />

      {/* Safety Notice */}
      <DisclaimerBanner />
    </div>
  );
};
